import { useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { FinancialAccount } from "@common/types/financialAccount";
import { Task } from "@common/types/task";
import { AccountsTableColumn } from "@common/types/views/accountsView";
import { IntegrationAccount } from "@common/types/integrationAccount";
import { Loader } from "@design-system/Loader/Loader";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useEntitiesContexts } from "@hooks/useEntitiesContexts";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import {
    accountsView,
    accountsViewConfig,
} from "@components/accounts/views/accountsViewConfig";
import { AccountsTableRow } from "@components/accounts/views/accountsView.types";
import { StatusCell } from "@components/accounts/views/cells/StatusCell";
import { BalanceCell } from "@components/accounts/views/cells/BalanceCell";
import { TaskBodyCommandIsland } from "../TaskBodyCommandIsland";
import taskBodyTableStyles from "../TaskBodyTable.module.scss";
import { getConnectionMetadata } from "../TaskBody.lib";

interface PlaidNewAccountsTaskBodyProps {
    selectedTask: Task;
}

const plaidNewAccountsColumns = [
    { id: AccountsTableColumn.ENTITY_OR_ACCOUNT },
    { id: AccountsTableColumn.BALANCE },
    { id: AccountsTableColumn.STATUS, options: { pin: "right" as const } },
];
const NEW_ACCOUNT_ROW_ID = "new-accounts";

const hasMatchingIntegrationAccount = ({
    financialAccount,
    connectionId,
}: {
    financialAccount: FinancialAccount;
    connectionId: string;
}) =>
    (financialAccount.integrationAccounts ?? []).some(
        (integrationAccount: IntegrationAccount) =>
            integrationAccount.integrationConnectionId === connectionId,
    );

const hasMatchingConnectionId = ({
    financialAccount,
    connectionId,
}: {
    financialAccount: FinancialAccount;
    connectionId: string;
}) =>
    (financialAccount.integrationAccounts ?? []).some(
        (integrationAccount: IntegrationAccount) =>
            String(integrationAccount.connectionId) === connectionId,
    );

export const PlaidNewAccountsTaskBody = ({
    selectedTask,
}: PlaidNewAccountsTaskBodyProps) => {
    const { connectionId, isIntegrationConnectionId } =
        getConnectionMetadata(selectedTask);

    const entitiesContext = useEntitiesContexts();
    const { isFetched: membersFetched } = useCurrentWorkspaceMembers();

    const plaidNewAccountsView = useMemo(
        () => ({
            ...accountsView,
            columns: plaidNewAccountsColumns,
        }),
        [],
    );
    const plaidNewAccountsViewConfig = useMemo(
        () => ({
            ...accountsViewConfig,
            columns: {
                ...accountsViewConfig.columns,
                [AccountsTableColumn.STATUS]: {
                    ...accountsViewConfig.columns[AccountsTableColumn.STATUS],
                    cell: (
                        context: CellContext<
                            ViewTableFeatures,
                            AccountsTableRow,
                            unknown
                        >,
                    ) =>
                        context.row.original.id === NEW_ACCOUNT_ROW_ID ? (
                            <ViewTableCell>Not connected</ViewTableCell>
                        ) : (
                            <StatusCell {...context} />
                        ),
                },
                [AccountsTableColumn.BALANCE]: {
                    ...accountsViewConfig.columns[AccountsTableColumn.BALANCE],
                    cell: (
                        context: CellContext<
                            ViewTableFeatures,
                            AccountsTableRow,
                            unknown
                        >,
                    ) =>
                        context.row.original.id === NEW_ACCOUNT_ROW_ID ? (
                            <ViewTableCell></ViewTableCell>
                        ) : (
                            <BalanceCell {...context} />
                        ),
                },
            },
            nesting: undefined,
        }),
        [],
    );

    const tableData = useMemo(() => {
        if (!connectionId) {
            return [];
        }

        const accounts = (entitiesContext ?? []).flatMap((entityContext) =>
            entityContext.financialAccounts
                .filter((financialAccount) =>
                    isIntegrationConnectionId
                        ? hasMatchingIntegrationAccount({
                              financialAccount,
                              connectionId,
                          })
                        : hasMatchingConnectionId({
                              financialAccount,
                              connectionId,
                          }),
                )
                .map((financialAccount): AccountsTableRow => ({
                    id: `account-${financialAccount.id}`,
                    entity: entityContext.entity,
                    account: financialAccount,
                    assignedMembers: [],
                    accounts: [],
                })),
        );

        const firstAccount = accounts[0];
        if (!firstAccount?.account) {
            return accounts;
        }

        accounts.push({
            id: NEW_ACCOUNT_ROW_ID,
            entity: firstAccount.entity,
            assignedMembers: [],
            accounts: [],
            account: {
                ...firstAccount.account,
                id: -firstAccount.account.id,
                name: "New account",
                disabled: false,
                balance: 0,
                balanceSyncedAt: null,
                transactionsSyncedAt: null,
                accountNumberMask: null,
                // Keep connection metadata so AccountCard can render institution icon.
                integrationAccounts: firstAccount.account.integrationAccounts,
            },
        });

        return accounts;
    }, [connectionId, entitiesContext, isIntegrationConnectionId]);

    if (!membersFetched || entitiesContext === undefined) {
        return <Loader />;
    }

    return (
        <>
            <ViewTable
                data={tableData}
                view={plaidNewAccountsView}
                config={plaidNewAccountsViewConfig}
                className={taskBodyTableStyles.viewTableWithoutTopBorder}
                rowBorders
                enableColumnResizing={false}
                fixedTopOffsetPx
                // The app header hides via a `body` class when fullscreen,
                // which only shifts this table's position, not its own size —
                // the auto top-offset measurement's ResizeObserver alone
                // wouldn't catch it, so force a re-measure on the flag itself.
                emptyView={() => (
                    <span>No accounts found for this connection.</span>
                )}
                footer={<TaskBodyCommandIsland selectedTask={selectedTask} />}
            />
        </>
    );
};
